import {
  DOMAIN_JOIN_STEP,
  IMMEDIATE_INSTALL_NOTE,
  INSTALL_STEP,
  NEW_ENVIRONMENT_NOTE,
  TRAVERSE_CONFIGURE_STEP,
  builtServerStep,
  sqlInstanceStep,
  type InstallTiming,
} from '@/data/applicationInstall'
import { isTraverse } from '@/data/productLines'
import { tlsPortProblem } from '@/data/tlsPort'
import { effectiveServerMode, type InfraWizardData } from '@/stores/infrastructureWizard'
import type { ApplicationAnswers } from '@/types/application'
import { applicationAnswerProblems, asksCreateFor } from '@/utils/applicationDetails'
import { dbNameStyle, licenseProblems, resolveServerName } from '@/utils/infraValidation'

export interface ApplicationInstallPlan {
  timing: InstallTiming
  /** What to tell the user: install right away, or install after the steps. */
  note: string
  /** The steps before the install, in order, ending with the install. Empty when it can be done right away. */
  steps: string[]
}

/** True when an environment is being created: either server is Dedicated. */
export function createsNewEnvironment(data: Pick<InfraWizardData, 'product' | 'rdsServer' | 'dbServer'>): boolean {
  return effectiveServerMode(data.product, data.rdsServer.mode, 'rds') === 'dedicated' || effectiveServerMode(data.product, data.dbServer.mode, 'db') === 'dedicated'
}

/** When the application can be installed, from the choices on the Infrastructure step. */
export function applicationInstallPlan(data: InfraWizardData): ApplicationInstallPlan {
  if (!createsNewEnvironment(data)) return { timing: 'immediately', note: IMMEDIATE_INSTALL_NOTE, steps: [] }

  const rdsNew = effectiveServerMode(data.product, data.rdsServer.mode, 'rds') === 'dedicated'
  const dbNew = effectiveServerMode(data.product, data.dbServer.mode, 'db') === 'dedicated'
  const nameOf = (role: 'rds' | 'db', style: 'host' | 'azure') => {
    const resolved = resolveServerName(data, role, style)
    return resolved.ok ? resolved.name : ''
  }

  const steps: string[] = []
  if (rdsNew) steps.push(builtServerStep('RDS server', nameOf('rds', 'host')))
  if (dbNew) {
    const style = dbNameStyle(data.level)
    steps.push(builtServerStep(style === 'azure' ? 'Azure SQL server' : 'SQL server', nameOf('db', style)))
  }
  if (rdsNew) steps.push(DOMAIN_JOIN_STEP)
  if (isTraverse(data.product)) steps.push(TRAVERSE_CONFIGURE_STEP)
  if (data.sqlInstance.required === true && data.sqlInstance.instanceName.trim() !== '') steps.push(sqlInstanceStep(data.sqlInstance.instanceName.trim()))
  steps.push(INSTALL_STEP)
  return { timing: 'after_new_environment', note: NEW_ENVIRONMENT_NOTE, steps }
}

/**
 * What is still to fill in on the Application (ERP) step: the TLS port (Traverse), the license of the environment's type (a product
 * line), and for Traverse the License Server Manager's Profile ID and database logins. Each is a message for the "To continue:" list.
 */
export function applicationProblems(
  data: Pick<InfraWizardData, 'product' | 'environment' | 'license'>,
  options: { traverse: boolean; tlsPort: string; answers?: ApplicationAnswers },
): string[] {
  const tls = options.traverse ? tlsPortProblem(options.tlsPort) : null
  const answers = options.traverse && options.answers ? applicationAnswerProblems(options.answers, { asksCreateFor: asksCreateFor(data) }) : []
  return [...(tls === null ? [] : [tls]), ...licenseProblems(data), ...answers]
}
